import React, { useContext, useState, useEffect } from 'react';
import app from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import { FirebaseContext } from '../idealline/Firebase';
import { buildCheckoutContext } from '../../utils/checkoutAttribution';
import { trackEvent } from '../../utils/analytics';

export const SubscriptionButton = ({ priceId, buttonText = 'Subscribe Now', className = '', checkoutIntent = 'unknown' }) => {
  const auth = useContext(FirebaseContext);
  const user = auth?.user;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check for existing subscription on mount and when user changes
  useEffect(() => {
    if (!user || user.isAnonymous) {
      setCheckingSubscription(false);
      setHasActiveSubscription(false);
      return;
    }

    // Check custom claims first (faster)
    const checkSubscriptionStatus = async () => {
      try {
        setCheckingSubscription(true);

        // Check if user has stripeRole claim (set by Stripe extension)
        const token = await user.getIdTokenResult();
        const role = token.claims?.stripeRole;

        if (role === 'paddock_pass') {
          setHasActiveSubscription(true);
          setCheckingSubscription(false);
          return;
        }

        // Fallback: check Firestore for active subscriptions
        const subsSnapshot = await app
          .firestore()
          .collection('customers')
          .doc(user.uid)
          .collection('subscriptions')
          .where('status', '==', 'active')
          .limit(1)
          .get();

        setHasActiveSubscription(!subsSnapshot.empty);
        setCheckingSubscription(false);
      } catch (e) {
        console.error('Error checking subscription status:', e);
        setCheckingSubscription(false);
      }
    };

    checkSubscriptionStatus();
  }, [user]);

  const handleManageSubscription = async () => {
    setError(null);
    setLoading(true);

    try {
      const fn = auth.app.functions && auth.app.functions();
      if (!fn || typeof fn.httpsCallable !== 'function') {
        throw new Error('Billing service unavailable');
      }

      const createPortalLink = fn.httpsCallable('ext-firestore-stripe-payments-createPortalLink');
      const { data } = await createPortalLink({
        returnUrl: window.location.origin + '/pricing'
      });

      if (data?.url) {
        window.location.assign(data.url);
      } else {
        throw new Error('Failed to get portal URL');
      }
    } catch (e) {
      console.error('Portal error:', e);
      setError(e.message || 'Unable to open customer portal');
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setError(null);
    if (!user || user.isAnonymous) {
      // If context not available or user not signed in, redirect to profile
      if (!auth || !auth.requireAuth) {
        const redirect = typeof window !== 'undefined' ? window.location.pathname : '/pricing';
        window.location = `/profile?redirect=${redirect}`;
        return;
      }
      // Otherwise use project helper
      return auth.requireAuth(async () => {});
    }

    try {
      setLoading(true);

      // Double-check for existing subscription before creating checkout session
      const subsSnapshot = await app
        .firestore()
        .collection('customers')
        .doc(user.uid)
        .collection('subscriptions')
        .where('status', '==', 'active')
        .limit(1)
        .get();

      if (!subsSnapshot.empty) {
        setError('You already have an active subscription. Redirecting to manage subscription...');
        setTimeout(() => handleManageSubscription(), 2000);
        return;
      }

      // Record intent separately so analytics can never alter or block Stripe checkout.
      try {
        app
          .firestore()
          .collection('customers')
          .doc(user.uid)
          .collection('checkout_intents')
          .add({
            ...buildCheckoutContext({ intent: checkoutIntent }),
            price: priceId,
            created_at: app.firestore.FieldValue.serverTimestamp(),
          })
          .catch((attributionError) => {
            console.warn('Checkout attribution could not be saved; continuing to Stripe checkout.', attributionError);
          });
      } catch (attributionError) {
        console.warn('Checkout attribution could not be saved; continuing to Stripe checkout.', attributionError);
      }

      // Create a checkout session via the known-working Firebase Stripe extension.
      const docRef = await app
        .firestore()
        .collection('customers')
        .doc(user.uid)
        .collection('checkout_sessions')
        .add({
          price: priceId,
          mode: 'subscription',
          success_url: window.location.origin + '/profile',
          cancel_url: window.location.href,
          allow_promotion_codes: true,
        });

      // Listen for the hosted URL created by the Stripe extension
      return docRef.onSnapshot((snap) => {
        const data = snap.data();
        if (data?.error) {
          trackEvent('checkout_error', {
            product: 'paddock_pass',
            placement: 'checkout',
            stage: 'checkout_session',
            intent: checkoutIntent,
            error_code: 'checkout_failed',
          });
          setError(data.error.message || 'Checkout failed.');
          setLoading(false);
        }
        if (data?.url) {
          trackEvent('begin_checkout', {
            product: 'paddock_pass',
            placement: 'checkout',
            stage: 'checkout_session',
            intent: checkoutIntent,
          });
          window.location.assign(data.url);
        }
      });
    } catch (e) {
      console.error('Checkout error', e);
      trackEvent('checkout_error', {
        product: 'paddock_pass',
        placement: 'checkout',
        stage: 'checkout_session',
        intent: checkoutIntent,
        error_code: 'checkout_failed',
      });
      setError(e.message || 'Checkout failed.');
      setLoading(false);
    }
  };

  // If checking subscription status, show loading state
  if (checkingSubscription) {
    return (
      <button disabled className={className}>
        Checking subscription...
      </button>
    );
  }

  // If user has active subscription, show manage button instead
  if (hasActiveSubscription) {
    return (
      <>
        <button
          onClick={handleManageSubscription}
          disabled={loading}
          className={className}
        >
          {loading ? 'Opening Portal…' : 'Manage Subscription'}
        </button>
        {error && (
          <p className="text-sm text-red-600 mt-2 text-center" role="alert">
            {error}
          </p>
        )}
      </>
    );
  }

  // Otherwise show normal checkout button
  return (
    <>
      <button onClick={handleCheckout} disabled={loading} className={className}>
        {loading ? 'Redirecting…' : buttonText}
      </button>
      {error && (
        <p className="text-sm text-red-600 mt-2 text-center" role="alert">
          {error}
        </p>
      )}
    </>
  );
};

export default SubscriptionButton;
